import { Box, Button, Flex, Heading, HStack, Spacer, Text, VStack, Image, Card, Grid, GridItem, Divider, Wrap, WrapItem, useMediaQuery, Show} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { GetCustomerFriendlyString } from "../../../../../helpers/EnumStringHelpers";
import { AppointmentStatus, IAppointment } from "../../../../../interfaces/jobsheets/appointments/IAppointment";
import { IJobsheet, JobsheetStatus } from "../../../../../interfaces/jobsheets/IJobsheet";
import { IWipLine, WiplineStatus } from "../../../../../interfaces/jobsheets/IWipline";
import * as webHelpers from "../../../../../helpers/WebHelpers";
import { GetDateTimeFromTimestamp, GetLongDateFormat } from "../../../../../helpers/DateTimeHelpers";
import { IDiscipline } from "../../../../../interfaces/jobsheets/IDiscipline";
import { MdPrint } from 'react-icons/md';
import { IWipLines } from "../../../../../interfaces/jobsheets/IWipline";
import useForceUpdate from "use-force-update";
import loadingGif from "../../../../../assets/loadingIcon.gif"
import "./index.css";
import Logo from "../../../../../assets/logo.svg";
import moment from "moment";
import { IRightToCancel } from "../../../../../interfaces/jobsheets/IRightToCancel";
import { Pricing } from "../Pricing";
import { IPoisonMeasurement } from "../../../../../interfaces/jobsheets/IPoison";
import { BiDownload } from "react-icons/bi";
import { IWorkReport } from "../../../../../interfaces/jobsheets/IWorkReport";
import { IVatCode, VatCodeType } from "../../../../../interfaces/accounting/VatCode";
import { useReactToPrint } from "react-to-print";

interface IProps {
    jobsheet: IJobsheet,
    rightToCancel: IRightToCancel | null,
    workReport: IWorkReport | null,
}

interface IAppointmentWork {
    appointment: IAppointment,
    wipLines: IWipQuestions[]
}

interface IWipQuestions {
    wipline: IWipLine,
    questions: IQuestionContent[]
}

interface IQuestionContent {
    answer: string,
    question: string,
    type: string,
}

interface IComplianceRecords {
    poisons: IComplianceRecord[],
    coshh: IComplianceRecord[]
}

interface IComplianceRecord {
    id: string,
    poison_id: string,
    wip_line_id: string,
    name: string,
    measurement: IPoisonMeasurement,
    quantity: number,
    location: string,
    notes: string,
    document_id: string
}


export const CompletedWorkSummary = (props: IProps) => {
    const [recordsLoaded, setRecordsLoaded] = useState<boolean>(false);
    const [records, setRecords] = useState<IComplianceRecord[]>([]);
    const [appointmentWork, setAppointmentWork] = useState<IAppointmentWork[]>([]);
    const [vatCode, setVatCode] = useState<IVatCode | null>(null);
    const [discipline, setDiscipline] = useState<string>("Unknown");
    const [wiplines, setWiplines] = useState<any[]>([]);
    const [ignore, setIgnore] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    const [firstTime, setFirstTime] = useState<boolean>(true);
    const [loadingText, setLoadingText] = useState<string>("loading")
    const forceUpdate = useForceUpdate();
    const [isLargerThan700] = useMediaQuery('(min-width: 700px)');

    const getWiplines = async (appointments: IAppointment[]) => {
        setAppointmentWork([]);
        let aws: IAppointmentWork[] = [];

        appointments.forEach((each: IAppointment) => {
            if (each.status === AppointmentStatus.Completed) {
                webHelpers.getAsync<IWipLine[]>("/api/sales/work/appointment/wiplines2?id=" + each.id).then((res) => {
                    if (res !== null) {
                        let aw: IAppointmentWork = {
                            appointment: each,
                            wipLines: []
                        }
                        res.forEach((wipline: IWipLine) => {
                            webHelpers.getAsync<any>("/api/sales/work/wip/cleardownpath?id=" + wipline.id).then(async (response) => {
                                if (response !== null) {
                                    let answers = await getFluidJson(response);
                                    aw.wipLines.push({
                                        wipline: wipline,
                                        questions: answers
                                    });
                                } else {
                                    aw.wipLines.push({
                                        wipline: wipline,
                                        questions: []
                                    })
                                }
                            }).catch((error) => {
                                console.error("We couldn't get the cleardown path, does it exist?")
                                aw.wipLines.push({
                                    wipline: wipline,
                                    questions: []
                                })
                            });;
                        });
                        aws.push(aw);
                    }
                })
            }
        })

        console.log(aws);
        setAppointmentWork(aws);
    }

    const GetRecords = async (jobNumber: string) => {
        return webHelpers.getAsync<IComplianceRecords>("/api/archive/document/all?jobNumber=" + jobNumber);
    }

    const getFluidJson = async (body: any) => {
        return await webHelpers.postAsync<any, IQuestionContent[]>(
            "/api/core/universal/fluid/responses/", body, "sales"
        )
    }

    useEffect(() => {
        webHelpers.getAsync<IAppointment[]>("/api/sales/work/appointment/history?job=" + props.jobsheet.id).then(res => {
            console.log("Got appointment history");
            if (res !== null) {
                getWiplines(res);
            } else {
                setAppointmentWork([]);
            }
        });

        let p3 = GetRecords(props.jobsheet.id).then(res => {
            if (res != null){
                setRecords(res.poisons);
            } else {
                setRecords([]);
            }

            setRecordsLoaded(true);
            console.log("Finished loading records");
        });

        webHelpers.getAsync<IVatCode>("/api/sales/accounting/vatcode?id=" + props.jobsheet.value_vat_code).then(res => {
            if (res !== null) {
                console.log("Vat rate " + res);
                setVatCode(res);
            }
        });
    }, []);

    useEffect(() => {
        if (recordsLoaded && vatCode){
            setLoading(false);
            console.log("Loading finished");
        }
    }, [recordsLoaded, vatCode])

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    const onDownloadFile = async (id: string) => {
        let response: Response = await webHelpers.getAsStreamAsync("/api/archive/document/get?id=" + id);
        if (response.ok) {
            let disposition: string | null = response.headers.get('content-disposition');
            let type: string | null = response.headers.get('content-type');
            let fileName = disposition?.split("filename=")[1];
            fileName = fileName?.substring(1, fileName.length - 1);

            if (type !== null){
                const file = new Blob([await response.blob()], {type: type});
                const fileURL = URL.createObjectURL(file);

                var fileLink = document.createElement('a');
                fileLink.href = fileURL;
                fileLink.download = fileName ?? "Test";

                fileLink.click();
            }
        } else {
            console.error("Unable to download file: " + response.json());
        }
    }

    const printPage = () => {
        let printDiv = document.getElementById("print-div");
        if (printDiv !== null) {
            printDiv.innerHTML = "";
            let nodes = ["details-card", "rtc-card", "works-card", "treatment-card", "wr-card", "invoice-card"];
            nodes.forEach(node => {
                let childDiv = document.getElementById(node);
                if (childDiv !== null) {
                    let pagebreak = document.createElement('div');
                    pagebreak.className = "page-break";
                    pagebreak.appendChild(childDiv.cloneNode(true));
                    printDiv?.appendChild(pagebreak);
                }
            });

            handlePrint();
        }
    }

    if (loading) {
        return (
            <Flex width={"100%"} justifyContent={"center"}>
                <Image srcSet={loadingGif} alt="loading..." boxSize={'400px'} objectFit={"cover"} alignSelf={"center"} display={"flex"}/> 
            </Flex>
        )
    }

    return (
        <div>
            <Box p={2}>
                <div className="print-only" ref={componentRef}>
                    <Flex mb={5} h={95} p={2} bg='var(--Able-blue)' w='full' boxShadow='lg'>
                        <Image mt='4px' src={Logo} alt='Able-Group' height='110px'/>
                        <Spacer/>
                        <Heading alignSelf='center' color='white'>Completed Works Report & Invoice</Heading>
                        <Spacer/>
                    </Flex>
                    <Box bg='var(--Able-yellow)' h='4px' mt='-20px'/> {/* God that's hacky - the border of a Box refuses to print for some reason */}

                    <Box id="print-div" mt={8}/>

                    <Flex bg='var(--Able-blue)' w='full' direction='row' align='end' justify='space-between' style={{position: 'fixed', left: 0, bottom: 0}}>
                        <Flex flexDir='column' p={2} h='full'>
                            <Text color='white' fontSize='sm'>Viabl Ltd trading as Able Group</Text>
                            <Text color='white' fontSize='sm'>Able House</Text>
                            <Text color='white' fontSize='sm'>39 Progress Road</Text>
                            <Text color='white' fontSize='sm'>Leigh-on-Sea</Text>
                            <Text color='white' fontSize='sm'>SS9 5PR</Text>
                        </Flex>

                        <Flex flexDir='column' p={2}>
                            <Text color='white' textAlign='right' fontSize='xs'>VAT Number: 626613543</Text>
                            <Text color='white' textAlign='right' fontSize='xs'>Company Registration Number (England & Wales): 11955942</Text>
                        </Flex>
                    </Flex>
                </div>

                <div className="screen-only">
                    <Flex direction={isLargerThan700 ? 'row' : 'column'} alignItems='center' justify='space-around'>
                        <Text fontSize={{base: 'sm', md: 'md'}} textAlign='center'>Below is a detailed summary of the work that has been completed. You can also print a version of this page for your records.</Text>
                        <Button mt={2} onClick={printPage} colorScheme='blue' variant='outline' rightIcon={<MdPrint />}>Print</Button>
                    </Flex>
                </div>

                <Box mt={5} className='screen-only'>
                    <Grid templateColumns='repeat(6, 1fr)'>
                        <GridItem colSpan={{base: 6, xl: 4}} m={2} colStart={{base: 0, xl: props.rightToCancel?.is_signed ? 0 : 2}}>
                            <Card id="details-card" fontSize={{base: 'sm', md: 'md'}} h='full'>
                                <Heading size='md' color='var(--Able-blue)' p={2} borderBottom='2px' borderColor='gray.300'>Job Details</Heading>
                                <Grid p={2} templateColumns={isLargerThan700 ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)'} rowGap={2} columnGap={4}>
                                    <Text><b>Reference No:</b></Text>
                                    <Text>{props.jobsheet.id}</Text>

                                    <Text><b>Customer Name:</b></Text>
                                    <Text>{props.jobsheet.customer?.Name}</Text>

                                    <Text><b>Completed Date:</b></Text>
                                    <Text>{moment(props.jobsheet.completed_date).format("DD/MM/yyyy")}</Text>

                                    <Text><b>Customer Type:</b></Text>
                                    <Text>{GetCustomerFriendlyString(props.jobsheet.customer?.Type)}</Text>

                                    <GridItem colSpan={isLargerThan700 ? 4 : 2} h={2}/>

                                    <Text><b>Billing Address: </b></Text>
                                    <Box>
                                        <Text textAlign='left'>{props.jobsheet.customer?.Billing.Address.line1}</Text>
                                        <Text textAlign='left'>{props.jobsheet.customer?.Billing.Address.line2}</Text>
                                        <Text textAlign='left'>{props.jobsheet.customer?.Billing.Address.line3}</Text>
                                        <Text textAlign='left'>{props.jobsheet.customer?.Billing.Address.line4}</Text>
                                        <Text textAlign='left'>{props.jobsheet.customer?.Billing.Address.post_code}</Text>
                                    </Box>

                                    <Text><b>Site Address: </b></Text>
                                    <Box>
                                        <Text textAlign='left'>{props.jobsheet.address.line1}</Text>
                                        <Text textAlign='left'>{props.jobsheet.address.line2}</Text>
                                        <Text textAlign='left'>{props.jobsheet.address.line3}</Text>
                                        <Text textAlign='left'>{props.jobsheet.address.line4}</Text>
                                        <Text textAlign='left'>{props.jobsheet.address.post_code}</Text>
                                    </Box>
                                </Grid>
                            </Card>
                        </GridItem>
                        {props.rightToCancel?.is_signed &&
                            <GridItem colSpan={{base: 6, xl: 2}} m={2}> 
                                <Card id="rtc-card" fontSize={{base: 'sm', md: 'md'}}>
                                    <Heading size='md' color='var(--Able-blue)' p={2} borderBottom='2px' borderColor='gray.300'>Right to Cancel</Heading>
                                    <Box p={2}>
                                        {props.rightToCancel.is_signed ?
                                            <Flex direction='column' w='full' align='center'>
                                                <Text textAlign='center' mb={2}>Your right to cancel has been signed acknowledging your rights and our terms and conditions.</Text>
                                                <Wrap justify='space-around' w='full'>
                                                    <Text><b>Signed by:</b> {props.rightToCancel?.signed?.signee}</Text>
                                                    <Text><b>Date Signed:</b> {moment(props.rightToCancel?.signed?.date).format("dddd DD MMMM yyyy")}</Text>
                                                </Wrap>
                                            </Flex>
                                        :
                                            <Text color='blue'>Your right to cancel has not yet been signed</Text>
                                        }
                                    </Box>
                                </Card>
                            </GridItem>
                        }
                        <GridItem colSpan={{base: 6, xl: 3}} rowSpan={2} m={2}>
                            <Card id="works-card" fontSize={{base: 'sm', md: 'md'}}>
                                <Heading size='md' color='var(--Able-blue)' p={2} borderBottom='2px' borderColor='gray.300'>Details of Work Completed</Heading>
                                <Box p={2}>
                                    {/* <Text w='full' textAlign='center'>The details of the work that was completed at each appointment</Text> */}

                                    {appointmentWork.map((apptWork: IAppointmentWork, index: number) => 
                                        <Box mt={1}>
                                            {appointmentWork.length > 1 ?
                                                <Text><b>Appointment {index + 1}: </b>{moment(apptWork.appointment.date).format("dddd DD MMMM yyyy")}</Text>
                                                :
                                                <Text><b>Appointment Date:</b> {moment(apptWork.appointment.date).format("dddd DD MMMM yyyy")}</Text>
                                            }
                                            <HStack my={2} spacing={10}>
                                                <Text><b>Time work started:</b> {GetDateTimeFromTimestamp(apptWork.appointment.allocation?.on_site_timestamp, "LTS")}</Text>
                                                <Text><b>Time work completed:</b> {GetDateTimeFromTimestamp(apptWork.appointment.allocation?.completed_timestamp, "LTS")}</Text>
                                            </HStack>

                                            <Box>
                                                <Text><b>Work Completed:</b></Text>  
                                                {apptWork.wipLines.map((each: IWipQuestions) =>
                                                    <VStack align='left' spacing={0} mt={1} mb={1}>
                                                        <Text>{each.wipline.quantity}&#215; {each.wipline.name}</Text>
                                                        {each.wipline.notes !== undefined && each.wipline.notes !== null  && /[a-zA-Z]/g.test(each.wipline.notes) === true &&
                                                            <Text mb={1}><Text as='span' fontWeight='semibold'>Notes: </Text>{each.wipline.notes}</Text>
                                                        }
                                                        {each.questions.map((element: IQuestionContent) => 
                                                            <Flex mb={2} direction='column'>                                                            
                                                                <Text as={"b"}>Q: {element.question}</Text>
                                                                <Text as={"i"}>A: {element.answer}</Text>
                                                            </Flex>
                                                        )}
                                                    </VStack>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Card>
                        </GridItem>
                        {props.workReport?.IsSigned &&
                            <GridItem colSpan={{base: 6, xl: 3}} m={2}>
                                <Card id="wr-card" fontSize={{base: 'sm', md: 'md'}}>
                                    <Heading size='md' color='var(--Able-blue)' p={2} borderBottom='2px' borderColor='gray.300'>Work Inspection</Heading>
                                    <Box p={2}>
                                        {props.workReport.IsSigned ? 
                                            <Box w='full'>
                                                <Text mb={2} textAlign='center'>The work inspection has been signed confirming that the work was inspected and has been carried out to a satisfactory standard.</Text>
                                                <Wrap justify='space-around' w='full'>
                                                    <Text><b>Signed by:</b> {props.workReport?.SigneeName}</Text>
                                                    <Text><b>Date Signed:</b> {moment(props.workReport?.SignedDate).format("dddd DD MMMM yyyy")}</Text>
                                                </Wrap>
                                            </Box>
                                            :
                                            <Text>The work inspection has not yet been signed</Text>
                                        }
                                    </Box>
                                </Card>
                            </GridItem>
                        }
                        {records.length > 0 &&
                            <GridItem colSpan={{base: 6, xl: 3}} m={2}>
                                <Card id="treatment-card" fontSize={{base: 'sm', md: 'md'}}>
                                    <Heading size='md' color='var(--Able-blue)' p={2} borderBottom='2px' borderColor='gray.300'>Treatment Details</Heading>
                                    <Box p={2} >
                                        <Wrap justifyItems='start'>
                                            {records.map((p: IComplianceRecord) => 
                                                <WrapItem>
                                                    <Box>
                                                        <VStack align='left' spacing={1} m={2}>
                                                            <Flex>
                                                                <Text as='b'>{p.quantity}</Text>
                                                                {p.measurement.abbreviation === null ? 
                                                                    <Text as='b'>{p.quantity > 1 ? p.measurement.plural_name : p.measurement.name}</Text>
                                                                    :
                                                                    <Text as='b'>{p.measurement.abbreviation}</Text>
                                                                }
                                                                <Text ml={2}> {p.name}</Text>
                                                            </Flex>
                                                            <Text><b>Location: </b>{p.location}</Text>
                                                            <Text><b>Notes: </b>{p.notes}</Text>
                                                            <div className='screen-only'>
                                                                <Button onClick={() => onDownloadFile(p.document_id)} leftIcon={<BiDownload/>} color={"var(--Able-blue)"}>Safety Infomation</Button>
                                                            </div>
                                                        </VStack>
                                                    </Box>
                                                </WrapItem>
                                            )}
                                        </Wrap>
                                    </Box>
                                </Card>
                            </GridItem>
                        }
                        <GridItem colSpan={{base: 6, lg: 3}} m={2} alignSelf='end'>
                            <Card id="invoice-card" fontSize={{base: 'sm', md: 'md'}}>
                                <Heading size='md' color='var(--Able-blue)' p={2} borderBottom='2px' borderColor='gray.300'>Invoice</Heading>
                                <Box p={2} w='full'>
                                    {/* <Heading mb={2} size='md' color='var(--Able-blue)' p={2} borderBottom='2px' borderColor='gray.200'>Pricing</Heading> */}

                                    <VStack pl={2} pr={2}>
                                        <Flex w='full' justify='space-between'>
                                            <Text>Labour:</Text>
                                            <Text>£{props.jobsheet.value_labour_nett.toFixed(2)}</Text>
                                        </Flex>
                                        <Flex w='full' justify='space-between'>
                                            <Text>Materials:</Text>
                                            <Text>£{props.jobsheet.value_materials_nett.toFixed(2)}</Text>
                                        </Flex>
                                        <Flex w='full' justify='space-between'>
                                            <Text>VAT at {vatCode?.rate ?? "--"}%:</Text>
                                            <Text>£{props.jobsheet.value_vat.toFixed(2)}</Text>
                                        </Flex>
                                        {vatCode?.type === VatCodeType.ReverseCharge && 
                                        <Text as='i' fontSize='sm' w='full'>Reverse Charged</Text>
                                        }
                                        {props.jobsheet.value_discount_requested !== 0 &&
                                            <Flex w='full' justify='space-between'>
                                                <Text>Discount Applied:</Text>
                                                {props.jobsheet.value_discount_is_percentage ?
                                                    <Text>{props.jobsheet.value_discount_requested}%</Text>
                                                    :
                                                    <Text>- £{String(props.jobsheet.value_discount_requested).slice(1)}</Text>
                                                }
                                            </Flex>
                                        }
                                        <Flex w='full' justify='space-between'>
                                            <Text>Total:</Text>
                                            <Text>£{props.jobsheet.value_gross.toFixed(2)}</Text>
                                        </Flex>
                                        {props.jobsheet.value_paid > 0 &&
                                            <Flex w='full' justify='space-between'>
                                                <Text>Balance Paid:</Text>
                                                <Text>£{props.jobsheet.value_paid.toFixed(2)}</Text>
                                            </Flex>
                                        }
                                        <Flex w='full' justify='space-between'>
                                            <Text as='b' color='var(--Able-blue)'>Balance Due:</Text>
                                            <Text as='b'>£{(props.jobsheet.value_gross - props.jobsheet.value_paid).toFixed(2)}</Text>
                                        </Flex>
                                    </VStack>
                                    {props.jobsheet.status < JobsheetStatus.Completed && 
                                        <Text as={"i"} textAlign={"right"} fontSize={"md"}>All pricing is subject to site survey</Text>
                                    }
                                </Box>
                            </Card>
                        </GridItem>
                    </Grid>
                </Box>
            </Box>
        </div>
        
    )
}